import { useCallback, useEffect, useRef, useState } from "react";
import { BASE_URL } from "../../constants/url";
import { FB_CONFIG_ID, loadFbSdk } from "./shared";

// WhatsApp Embedded Signup + status/toggle/disconnect for the agency.
export default function useWhatsApp({ agencyId, token }) {
  // ---------- WhatsApp state ----------
  const [waStatus, setWaStatus] = useState(null); // null = loading, false = not connected, object = connected
  const [waLoading, setWaLoading] = useState(true);
  const [waActionLoading, setWaActionLoading] = useState(false); // spinner for connect/disconnect/toggle
  const [waError, setWaError] = useState(null);
  const [waSuccess, setWaSuccess] = useState(null);

  // Meta posts WA_EMBEDDED_SIGNUP from the signup popup while FB.login hands
  // back the auth code from its own callback — either can land first, so both
  // halves are stashed in refs and the connect call fires once both are in.
  const waAuthCodeRef = useRef(null);
  const waSignupRef = useRef(null); // { waba_id, phone_number_id }
  const waExistingNumberRef = useRef(false);
  const waFinishTimerRef = useRef(null);

  // ─── WhatsApp: status check on page load ─────────────────────
  const fetchWaStatus = useCallback(async () => {
    if (!token || !agencyId) return;
    try {
      const res = await fetch(
        `${BASE_URL}api/agencywhatsapp/status`,
        { headers: { Authorization: `Bearer ${token}`, userid: agencyId, "Content-Type": "application/json" } }
      );
      const data = await res.json();
      setWaStatus(data.connected ? data : false);
    } catch {
      setWaStatus(false);
    } finally {
      setWaLoading(false);
    }
  }, [token, agencyId]);

  useEffect(() => {
    fetchWaStatus();
  }, [fetchWaStatus]);

  // ─── WhatsApp: preload the Facebook SDK so FB.login keeps the ─
  //     user gesture (avoids popup blockers on first click)
  useEffect(() => {
    loadFbSdk().catch(() => {
      // retried on click; error surfaced there
    });
  }, []);

  // Complete the connection: Meta returns the auth code from FB.login and the
  // WABA / phone number ids from the Embedded Signup session, and the backend
  // needs all three to finish onboarding.
  const completeWaOnboarding = useCallback(
    async (code, signup, existingNumber) => {
      setWaActionLoading(true);
      setWaError(null);
      try {
        const res = await fetch(`${BASE_URL}api/whatsapp/onboarding/complete`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            userid: agencyId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code,
            waba_id: signup?.waba_id ?? null,
            phone_number_id: signup?.phone_number_id ?? null,
            existing_number: !!existingNumber,
          }),
        });
        const data = await res.json();
        if (data.success) {
          setWaStatus({
            connected: true,
            businessName: data.businessName,
            displayPhoneNumber: data.displayPhoneNumber,
            connectedAt: data.connectedAt,
            isEnabled: true,
          });
          setWaSuccess("WhatsApp connected successfully!");
        } else {
          setWaError(data.error || "Connection failed. Please try again.");
        }
      } catch {
        setWaError("Connection failed. Please check your connection and try again.");
      } finally {
        setWaActionLoading(false);
      }
    },
    [agencyId, token]
  );

  // FB.login and the postMessage hand-off race each other, so whoever arrives
  // last kicks off the connect call.
  // The coexistence (existing number) flow may not return a phone_number_id —
  // the backend resolves it from the waba_id after the token exchange (GET
  // /{waba_id}/phone_numbers), so a missing phone number must never block
  // the hand-off — waba_id alone is enough to proceed.
  const finishWaOnboardingIfReady = useCallback(() => {
    const code = waAuthCodeRef.current;
    const signup = waSignupRef.current;
    // phone_number_id is intentionally optional: the coexistence flow often
    // omits it and the backend looks it up from waba_id post-exchange.
    if (!code || !signup?.waba_id) return;
    waAuthCodeRef.current = null;
    waSignupRef.current = null;
    if (waFinishTimerRef.current) {
      clearTimeout(waFinishTimerRef.current);
      waFinishTimerRef.current = null;
    }
    completeWaOnboarding(code, signup, waExistingNumberRef.current);
  }, [completeWaOnboarding]);

  // ─── WhatsApp: Embedded Signup session events (postMessage) ───
  useEffect(() => {
    function handleEmbedMessage(event) {
      // Meta only posts Embedded Signup events from facebook.com and subdomains.
      if (!/^https?:\/\/([a-z0-9-]+\.)*facebook\.com$/i.test(event.origin || "")) return;

      let payload = event.data;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch {
          return;
        }
      }
      if (!payload || payload.type !== "WA_EMBEDDED_SIGNUP") return;

      // Keep the raw payload in the console for diagnosing Embedded Signup
      // issues (e.g. which FINISH* variant the session actually sent).
      console.log("WA_EMBEDDED_SIGNUP", payload);

      // The existing-number (coexistence) flow finishes with
      // FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING rather than FINISH, so match
      // any FINISH* variant instead of the exact string.
      if (typeof payload.event === "string" && payload.event.startsWith("FINISH")) {
        const session = payload.data || {};
        // Key names differ between the new-number and coexistence payloads, and
        // the coexistence payload may omit the phone number entirely — the
        // backend derives it from waba_id when it's missing.
        waSignupRef.current = {
          waba_id: session.waba_id ?? session.wabaId ?? session.whatsapp_business_account_id ?? null,
          phone_number_id: session.phone_number_id ?? session.phoneNumberId ?? null,
        };
        finishWaOnboardingIfReady();
      } else if (payload.event === "CANCEL") {
        // Meta reports which step the user backed out of — useful when
        // diagnosing why a session never reaches a FINISH* event.
        console.log(
          "User cancelled at step:",
          (payload.data && payload.data.current_step) || "unknown"
        );
        waAuthCodeRef.current = null;
        waSignupRef.current = null;
        if (waFinishTimerRef.current) {
          clearTimeout(waFinishTimerRef.current);
          waFinishTimerRef.current = null;
        }
        setWaActionLoading(false);
        setWaError("WhatsApp setup was cancelled. Please try again.");
      } else if (payload.event === "error") {
        setWaActionLoading(false);
        setWaError(payload.error_message || "WhatsApp setup failed. Please try again.");
      }
    }

    window.addEventListener("message", handleEmbedMessage);
    return () => window.removeEventListener("message", handleEmbedMessage);
  }, [finishWaOnboardingIfReady]);

  // Drop a pending safety timer if the user leaves the page mid-setup.
  useEffect(
    () => () => {
      if (waFinishTimerRef.current) clearTimeout(waFinishTimerRef.current);
    },
    []
  );

  // ─── WhatsApp: connect (Meta Embedded Signup via FB.login) ────
  // `existingNumber` picks the Embedded Signup flow: an already-connected
  // WhatsApp Business number, or a new number / new WABA.
  const handleWaConnect = async (existingNumber = false) => {
    waExistingNumberRef.current = !!existingNumber;
    waAuthCodeRef.current = null;
    waSignupRef.current = null;
    setWaActionLoading(true);
    setWaError(null);
    try {
      await loadFbSdk();
      window.FB.login(
        (response) => {
          const code = response?.authResponse?.code;
          if (!code) {
            setWaActionLoading(false);
            setWaError("WhatsApp setup was cancelled. Please try again.");
            return;
          }
          waAuthCodeRef.current = code;
          // FB.login often fires before the Embedded Signup message lands, so
          // try now and again on FINISH. The timer stops the spinner from
          // hanging forever if the session payload never arrives.
          finishWaOnboardingIfReady();
          if (waFinishTimerRef.current) clearTimeout(waFinishTimerRef.current);
          waFinishTimerRef.current = setTimeout(() => {
            if (!waSignupRef.current) {
              setWaActionLoading(false);
              setWaError(
                "We couldn't finish the WhatsApp setup. Please retry and complete every step in the popup."
              );
            }
          }, 60000);
        },
        {
          config_id: FB_CONFIG_ID,
          response_type: "code",
          override_default_response_type: true,
          extras: waExistingNumberRef.current
            ? {
                setup: {},
                featureType: "whatsapp_business_app_onboarding",
                sessionInfoVersion: "3",
              }
            : { setup: {}, sessionInfoVersion: "3" },
        }
      );
    } catch {
      setWaActionLoading(false);
      setWaError("Could not load Facebook. Please check your network and try again.");
    }
  };

  // ─── WhatsApp: disconnect ─────────────────────────────────────
  const handleWaDisconnect = async () => {
    setWaActionLoading(true);
    setWaError(null);
    try {
      const res = await fetch(`${BASE_URL}api/whatsapp/onboarding/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, userid: agencyId },
      });
      const data = await res.json();
      if (data.success) {
        setWaStatus(false);
        setWaSuccess("WhatsApp disconnected successfully.");
      } else {
        setWaError("Failed to disconnect. Please try again.");
      }
    } catch {
      setWaError("Failed to disconnect. Please try again.");
    } finally {
      setWaActionLoading(false);
    }
  };

  // ─── WhatsApp: toggle (pause / resume) ────────────────────────
  const handleWaToggle = async () => {
    setWaActionLoading(true);
    setWaError(null);
    try {
      const res = await fetch(`${BASE_URL}api/whatsapp/onboarding/toggle`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, userid: agencyId },
      });
      const data = await res.json();
      if (data.success) {
        setWaStatus((prev) => ({ ...prev, isEnabled: data.isEnabled }));
        setWaSuccess(data.isEnabled ? "Automation resumed." : "Automation paused.");
      } else {
        setWaError("Failed to update automation. Please try again.");
      }
    } catch {
      setWaError("Failed to update automation. Please try again.");
    } finally {
      setWaActionLoading(false);
    }
  };

  // ─── WhatsApp: re-fetch after reconnect (same flow as connect) ─
  const handleWaReconnect = () => {
    handleWaConnect(true);
  };

  const waConnected = !!(waStatus && waStatus.connected);

  return {
    waStatus,
    waLoading,
    waConnected,
    waActionLoading,
    waError,
    setWaError,
    waSuccess,
    setWaSuccess,
    handleWaConnect,
    handleWaDisconnect,
    handleWaToggle,
    handleWaReconnect,
    refreshWaStatus: fetchWaStatus,
  };
}
